import { useInertiaCable } from "@inertia-cable/react";
import { router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import type Account from "@travily/types/models/Account";
import type Message from "@travily/types/models/MessagesMessage";
import { Icon } from "@ui/components/Icon";
import type { MessageReaction } from "@ui/components/ReactionPicker";
import { TypingIndicator } from "@ui/components/TypingIndicator";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { ConversationDetailSkeleton } from "@/components/ConversationDetailSkeleton/ConversationDetailSkeleton";
import { MessageComposer } from "@/components/MessageComposer/MessageComposer";
import { MessageList } from "@/components/MessageList/MessageList";
import { useConversationSend } from "@/hooks/use-conversation-send";
import type { UploadFile } from "@/hooks/use-direct-upload-multiple";
import { useMessageDock } from "@/hooks/use-message-dock";
import { useMessageReactions } from "@/hooks/use-message-reactions";
import { useTypingIndicator } from "@/hooks/use-typing-indicator";
import {
  closeDockedChat,
  type DockedChat,
  getDockedChat,
  isMessagesRoute,
  setDockedChatRendered,
} from "@/lib/message-dock-store";
import { messageThreadHref } from "@/lib/routes";
import type { DockedConversation, SharedProps } from "@/types";

// The dock's thread rides inside `docked_conversation`, and its failures speak through `flash`;
// nothing else on the host page has anything to do with a message sent from the corner.
const DOCK_RELOAD_PROPS = ["docked_conversation", "flash"];

const messageDock = tv({
  slots: {
    // The dock shares the top bar's chrome band: above the Dock/BottomBar it overlaps, below
    // sheets so the thread's own reaction picker still wins. Scale in theme.css.
    root: "fixed right-8 bottom-0 z-(--z-chrome-top) flex w-[360px] flex-col overflow-hidden rounded-t-[20px] shadow-md",
    header: "flex items-center gap-3 bg-red-400 px-5 pt-[19px] pb-4",
    title: "min-w-0 flex-1 truncate text-start font-semibold text-lg/6 text-white",
    control:
      "flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-white outline-none transition-colors hover:bg-red-500 focus-visible:ring-2 focus-visible:ring-white",
    badge:
      "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-white px-1.5 font-semibold text-[11px] text-red-500 leading-none",
    body: "flex min-h-0 flex-1 flex-col bg-white",
  },
  variants: {
    minimised: {
      // Open: the Figma panel. Minimised: the header bar alone sizes the box, so no height at all.
      false: { root: "h-[520px] max-h-[calc(100dvh-2rem)]" },
      true: {},
    },
  },
});

export interface MessageDockPanelProps {
  account: Account;
  attachments: UploadFile[];
  currentUserId: number;
  isMinimised: boolean;
  isSending: boolean;
  messages: Message[] | undefined;
  messageValue: string;
  onAddFiles: (files: FileList) => void;
  onClose: () => void;
  onMessageChange: (value: string) => void;
  onMinimisedChange: (minimised: boolean) => void;
  onRemoveFile: (id: string) => void;
  onSendMessage: () => void;
  onToggleReaction: (messageId: number, emoji: string) => void;
  onTyping?: () => void;
  reactions: Record<string, MessageReaction[]>;
  /** When the other side last opened the thread. Null until they have. */
  readAt: string | null;
  /** Names for the read receipt. Empty when the counterpart has no display name to show. */
  readers: string[];
  /** The counterpart's typing bubble, shown at the foot of the thread. */
  typingIndicator?: ReactNode;
  /** Messages that landed while minimised. Badged on the collapsed bar. */
  unreadCount: number;
}

/**
 * The docked chat panel — a third composition of the shared thread parts, after the messages page
 * and its mobile drill-down, wrapped in its own corner chrome.
 *
 * Deliberately not modal: no focus trap, no scroll lock, no outside-click dismiss. The page
 * underneath stays fully usable while the conversation runs in the corner, which is the whole point
 * of docking it.
 */
export function MessageDockPanel({
  account,
  attachments,
  currentUserId,
  isMinimised,
  isSending,
  messages,
  messageValue,
  onAddFiles,
  onClose,
  onMessageChange,
  onMinimisedChange,
  onRemoveFile,
  onSendMessage,
  onToggleReaction,
  onTyping,
  reactions,
  readAt,
  readers,
  typingIndicator,
  unreadCount,
}: MessageDockPanelProps) {
  const styles = messageDock({ minimised: isMinimised });
  const name = account.display_name ?? "";
  // The badge is decorative, so the count has to reach a screen reader through the only control
  // that state offers.
  const restoreLabel =
    unreadCount > 0
      ? t("components.views.shared.message_dock.restore_unread", { count: unreadCount, name })
      : t("components.views.shared.message_dock.restore", { name });
  const title = (
    <>
      {/* 28px is not on the Avatar size scale: `size-7` takes the box while the 32 variant keeps the
          matching fallback type scale. The ring paints outside it, so the row still measures 32. */}
      <AccountAvatar account={account} className="size-7 ring-2 ring-white" shape="circle" size="32" />
      <span className={styles.title()}>{name}</span>
    </>
  );

  return (
    <aside aria-label={t("components.views.shared.message_dock.panel_label", { name })} className={styles.root()}>
      <header className={styles.header()}>
        {isMinimised ? (
          <Button
            aria-label={restoreLabel}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-white"
            onPress={() => onMinimisedChange(false)}
          >
            {title}
          </Button>
        ) : (
          title
        )}
        {isMinimised && unreadCount > 0 ? (
          <span aria-hidden="true" className={styles.badge()}>
            {unreadCount}
          </span>
        ) : null}
        {isMinimised ? null : (
          <Button
            aria-label={t("components.views.shared.message_dock.minimize")}
            className={styles.control()}
            onPress={() => onMinimisedChange(true)}
          >
            <Icon name="minus" size="20" />
          </Button>
        )}
        <Button aria-label={t("common.actions.close")} className={styles.control()} onPress={onClose}>
          <Icon name="exit" size="20" />
        </Button>
      </header>
      {isMinimised ? null : (
        <div className={styles.body()}>
          <MessageList
            currentUserId={currentUserId}
            messages={messages}
            onToggleReaction={onToggleReaction}
            reactions={reactions}
            readAt={readAt}
            readers={readers}
            typingIndicator={typingIndicator}
          />
          <div className="px-4 pb-safe-[1rem]">
            <MessageComposer
              attachments={attachments}
              isSending={isSending}
              messageValue={messageValue}
              onAddFiles={onAddFiles}
              onMessageChange={onMessageChange}
              onRemoveFile={onRemoveFile}
              onSendMessage={onSendMessage}
              onTyping={onTyping}
            />
          </div>
        </div>
      )}
    </aside>
  );
}

export interface MessageDockLoadingProps {
  handle: string;
  onClose: () => void;
}

/** The panel between the press and the thread arriving, so the press is never silent. */
export function MessageDockLoading({ handle, onClose }: MessageDockLoadingProps) {
  const styles = messageDock({ minimised: false });

  return (
    <aside aria-busy="true" aria-label={t("components.views.shared.message_dock.opening")} className={styles.root()}>
      <header className={styles.header()}>
        <span className={styles.title()}>@{handle}</span>
        <Button aria-label={t("common.actions.close")} className={styles.control()} onPress={onClose}>
          <Icon name="exit" size="20" />
        </Button>
      </header>
      <div className={styles.body()}>
        <ConversationDetailSkeleton />
      </div>
    </aside>
  );
}

interface DockedThreadProps {
  chat: DockedChat;
  currentUserId: number;
  docked: DockedConversation;
  onClose: () => void;
  onDraftChange: (draft: string) => void;
  onMinimisedChange: (minimised: boolean) => void;
  onSeenMessageIdChange: (id: number | null) => void;
}

/**
 * Wires the docked thread to the server. Keyed on the handle by its parent, so swapping chats
 * resets the composer, the pending uploads and the unread watermark instead of carrying them over.
 */
function DockedThread({
  chat,
  currentUserId,
  docked,
  onClose,
  onDraftChange,
  onMinimisedChange,
  onSeenMessageIdChange,
}: DockedThreadProps) {
  const { cable_stream, conversation, counterpart_read_at, other_account, typing_stream } = docked;
  const conversationSqid = conversation?.sqid ?? null;
  useInertiaCable(cable_stream, { only: ["docked_conversation"] });

  const { isSending, messages, messageValue, send, setMessageValue, uploads } = useConversationSend({
    accountHandle: chat.handle,
    conversationSqid,
    initialMessageValue: chat.draft,
    messages: docked.messages,
    reloadProps: DOCK_RELOAD_PROPS,
  });
  const { reactions, toggle: toggleReaction } = useMessageReactions(
    conversationSqid,
    docked.reactions,
    DOCK_RELOAD_PROPS,
  );

  useEffect(() => {
    onDraftChange(messageValue);
  }, [messageValue, onDraftChange]);

  // Nothing on the wire says which messages this viewer has seen — `counterpart_read_at` is the
  // other side's marker — so minimising freezes a watermark and the badge counts whatever landed
  // behind the collapsed bar. An optimistic id is negative and would put the mark below every real
  // message, so only a persisted one may move it.
  const { seenMessageId } = chat;
  useEffect(() => {
    if (chat.minimised) return;

    const latest = messages?.at(-1)?.id;
    if (latest !== undefined && latest > 0) onSeenMessageIdChange(latest);
  }, [chat.minimised, messages, onSeenMessageIdChange]);

  const unreadCount = useMemo(() => {
    if (!chat.minimised) return 0;
    return (messages ?? []).filter(
      (message) => message.sender_id !== currentUserId && (seenMessageId === null || message.id > seenMessageId),
    ).length;
  }, [chat.minimised, currentUserId, messages, seenMessageId]);

  // Whether a receipt shows is decided by the read marker alone; the name is only its copy.
  const readers = useMemo(
    () => (other_account.display_name ? [other_account.display_name] : []),
    [other_account.display_name],
  );

  const { isPeerTyping, onTyping } = useTypingIndicator({ currentUserId, messages, stream: typing_stream });
  const typingIndicator = isPeerTyping ? (
    <TypingIndicator
      label={t("components.views.shared.message_list.typing", { name: other_account.display_name ?? "" })}
    />
  ) : null;

  return (
    <MessageDockPanel
      account={other_account}
      attachments={uploads.files}
      currentUserId={currentUserId}
      isMinimised={chat.minimised}
      isSending={isSending}
      messages={messages}
      messageValue={messageValue}
      onAddFiles={uploads.addFiles}
      onClose={onClose}
      onMessageChange={setMessageValue}
      onMinimisedChange={onMinimisedChange}
      onRemoveFile={uploads.removeFile}
      onSendMessage={send}
      onToggleReaction={toggleReaction}
      onTyping={onTyping}
      reactions={reactions}
      readAt={counterpart_read_at}
      readers={readers}
      typingIndicator={typingIndicator}
      unreadCount={unreadCount}
    />
  );
}

/**
 * Mounts the docked chat wherever the app chrome persists, and owns fetching its thread: the panel
 * renders from the `docked_conversation` shared prop, which the server attaches only to a visit
 * carrying the dock's header, so opening one has to go and ask.
 */
export function MessageDock() {
  const { chat, close, setDraft, setMinimised, setSeenMessageId } = useMessageDock();
  const { props, url } = usePage<SharedProps>();
  const { current_user, docked_conversation } = props;
  const isDesktop = useBreakpoint("lg");

  // /messages owns the thread — two live copies would fight over the read marker — and a 360px
  // panel below `lg` covers half the viewport.
  const isDockable = Boolean(chat && isDesktop && !isMessagesRoute(url));
  // A response for a since-closed or since-swapped dock is not this dock's thread.
  const docked = docked_conversation?.handle === chat?.handle ? docked_conversation : null;

  // Only a panel that is on screen may tell the server its thread is being read, and the store —
  // which stamps that claim outside React — cannot see the route or the viewport from where it sits.
  useEffect(() => {
    setDockedChatRendered(isDockable);
    return () => setDockedChatRendered(false);
  }, [isDockable]);

  useDockedConversation(isDockable ? (chat?.handle ?? null) : null, docked !== null);

  if (!isDockable || !chat) return null;
  // A signed-out reader resolves no thread and is sent to the messages page, so this is the panel
  // they see for that one round trip rather than a dock that never fills.
  if (!docked || !current_user) return <MessageDockLoading handle={chat.handle} onClose={close} />;

  return (
    <DockedThread
      key={chat.handle}
      chat={chat}
      currentUserId={current_user.id}
      docked={docked}
      onClose={close}
      onDraftChange={setDraft}
      onMinimisedChange={setMinimised}
      onSeenMessageIdChange={setSeenMessageId}
    />
  );
}

/**
 * Fetches the open dock's thread whenever the page in hand does not already carry it — on opening,
 * and again after a history restore replays a page entry captured with a different dock.
 *
 * One attempt per handle: a server that will not serve the thread here answers a bare null (a
 * restricted reader, or messaging not yet open to them), and then the messages page — which carries
 * that policy and says so properly — is where the reader belongs.
 */
function useDockedConversation(handle: string | null, isLoaded: boolean): void {
  const attemptedHandle = useRef<string | null>(null);

  useEffect(() => {
    if (!handle) return;
    if (isLoaded) {
      attemptedHandle.current = null;
      return;
    }
    if (attemptedHandle.current === handle) return;
    attemptedHandle.current = handle;

    const href = messageThreadHref(handle, getDockedChat()?.draft);
    router.reload({
      only: ["docked_conversation"],
      onSuccess: (page) => {
        if (page.props.docked_conversation) return;

        closeDockedChat();
        router.visit(href);
      },
      onError: closeDockedChat,
    });
  }, [handle, isLoaded]);
}
